import {
  TKV2_ORDER_ATTACHMENT_API,
  extractPropertyUnitRateFeatures,
  isDefined,
} from '@aloft/toolkit-shared';
import { useQueryClient } from '@tanstack/react-query';

import { useEffect, useRef, useState } from 'react';

import {
  fetchGetSubjectData,
  getTkv2UnitRateModelsApiQuery,
  useGetTkv2UnitRateModelsAPI,
  usePostTkv2UnitRateModelsAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { setPropertyData } from '../../redux/features/propertyDataSlice.ts';
import { featuresSelectors } from '../../redux/features/selectors/featuresSelectors.ts';
import { selectPropertyDataById } from '../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectUnitRates } from '../../redux/features/selectors/unitRatesSelectors/selectUnitRates.ts';
import { setUnitRates, unitRatesMutationComplete } from '../../redux/features/unitRatesSlice.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { SubjectPropertyDataState } from '../../redux/types.ts';

/**
 * Ensures unit rate models are computed for an order once all required subject
 * feature fields are present. Always fires a POST on first load to refresh
 * sensitivity against the current comp set. The backend reuses any existing
 * regression result and handles autofilling subject unit rates (preserving
 * manually-entered values). After the POST resolves, cache invalidation causes
 * the GET to re-run and Redux is hydrated with the latest results.
 *
 * Returns `{ isPending }` so callers can show a loading indicator while the
 * computation is in-flight. The pending state is set to `true` as soon as all
 * required features are present, before the POST even fires, to avoid a brief
 * flash of the grid while the effect is scheduled.
 */
export const useUnitRateModelsSyncDriver = (orderID: string) => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { mutateAsync: postUnitRateModels } = usePostTkv2UnitRateModelsAPI();
  const { selectShouldShowAdjustmentRegression } = featuresSelectors;
  const shouldShowAdjustmentRegression = useAppSelector(selectShouldShowAdjustmentRegression);
  const { fetcherOptions } = useToolkitContext();

  const subjectPropertyData = useAppSelector((state) =>
    selectPropertyDataById(state, 'subject'),
  ) as SubjectPropertyDataState | undefined;

  let ready = false;
  if (subjectPropertyData) {
    const features = extractPropertyUnitRateFeatures(subjectPropertyData.subjectData);
    ready = Object.values(features).every(isDefined);
  }

  const unitRates = useAppSelector(selectUnitRates);

  const [loadingDone, setLoadingDone] = useState(false);
  const hasTriggered = useRef(false);

  const { data: unitRateData } = useGetTkv2UnitRateModelsAPI(
    { pathParams: { orderID } },
    {
      enabled: ready && shouldShowAdjustmentRegression && !unitRates.syncState && loadingDone,
      retry: false,
    },
  );

  // Hydrate Redux after the POST completes and the GET re-fetches.
  useEffect(() => {
    if (!unitRateData || unitRates.syncState) return;

    const parsedData = TKV2_ORDER_ATTACHMENT_API.UNIT_RATE_MODELS.GET.RESPONSE.parse(unitRateData);
    dispatch(setUnitRates(parsedData.body));
  }, [unitRateData]);

  // Always POST on first load once all required features are present.
  useEffect(() => {
    if (!ready || hasTriggered.current || !shouldShowAdjustmentRegression) return;
    hasTriggered.current = true;

    const run = async () => {
      await postUnitRateModels({ pathParams: { orderID } });

      await queryClient.invalidateQueries({
        queryKey: getTkv2UnitRateModelsApiQuery({ pathParams: { orderID } }).queryKey,
      });

      const subjectData = await fetchGetSubjectData({
        ...fetcherOptions,
        pathParams: { orderID },
      });

      dispatch(
        setPropertyData({
          id: 'subject',
          subjectData,
        } as SubjectPropertyDataState),
      );
    };

    run()
      .then(() => dispatch(unitRatesMutationComplete({ status: 'success' })))
      .catch(() => dispatch(unitRatesMutationComplete({ status: 'error' })))
      .finally(() => setLoadingDone(true));
  }, [ready, orderID, postUnitRateModels, queryClient, dispatch, shouldShowAdjustmentRegression]);

  const isPending = !loadingDone && ready && shouldShowAdjustmentRegression;

  return { isPending };
};
